/**
 * A seção das fotos do signatário na análise preliminar (E80.F1.T06 — ADR-0079).
 *
 * Entra logo depois do protocolo do caso, que é onde o owner pediu: importado o
 * protocolo, o perito anexa as fotos do rosto que a conferência (E80.F2) vai
 * comparar.
 *
 * Dois grupos, cada um com VÁRIAS fotos e uma escolhida (§2.1). Trocar a
 * escolhida é um clique e não apaga nada — a preterida continua na lista, e é
 * isso que permite conferir a segunda candidata sem perder a primeira.
 *
 * O estado vive aqui e a galeria é burra: as duas ações que mexem na mesma
 * invariante (anexar e escolher) precisam recarregar o caso inteiro depois de
 * escrever, porque escolher no grupo despromove a irmã — e uma tela que
 * atualizasse só a linha clicada mostraria duas escolhidas.
 */
import { useCallback, useEffect, useState } from 'react';

import { Blueprint } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { ExameEletronicoError } from '@/lib/exame_eletronico';
import {
  anexarFotoDoSignatario,
  elegerFotoDoSignatario,
  listarFotosDoSignatario,
  recortarFotoDoSignatario,
  removerFotoDoSignatario,
  type CaixaDoRecorte,
  type FotoDoSignatarioData,
  type FotosDoSignatarioData,
  type GrupoDaFoto,
  type RotacaoDaFoto,
} from '@/lib/fotos_do_signatario';

import { GrupoDeFotos, type AcaoNaFoto } from './GrupoDeFotos';
import { RecorteDaFoto } from './RecorteDaFoto';

const t = ptBR.exameEletronico.fotosDoSignatario;

const _MENSAGEM: Record<string, string> = {
  arquivo_nao_suportado: t.erros.arquivo_nao_suportado,
  arquivo_vazio: t.erros.arquivo_vazio,
  arquivo_muito_grande: t.erros.arquivo_muito_grande,
  arquivo_nao_e_imagem: t.erros.arquivo_nao_e_imagem,
  not_found: t.erros.foto_nao_encontrada,
};

function mensagemDoErro(err: unknown): string {
  if (err instanceof ExameEletronicoError) return _MENSAGEM[err.reason] ?? t.erros.generico;
  return t.erros.generico;
}

export interface FotosDoSignatarioProps {
  caseId: string;
  /** O perito concluiu alguma peça: o material do exame não muda mais. */
  concluido: boolean;
  /**
   * Avisa que as fotos do caso mudaram (E80.F2).
   *
   * Quem escuta é a conferência facial, logo abaixo: trocar a escolhida muda o
   * par, e o parecer já emitido passa a ser sobre outras fotos. Sem o aviso, a
   * marca de "a escolha mudou" só apareceria depois de recarregar a página.
   */
  onMudou?: () => void;
}

export function FotosDoSignatario({
  caseId,
  concluido,
  onMudou,
}: FotosDoSignatarioProps): JSX.Element {
  const client = useApiClient();
  const [dados, setDados] = useState<FotosDoSignatarioData | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviandoNoGrupo, setEnviandoNoGrupo] = useState<GrupoDaFoto | null>(null);
  const [acaoPorFoto, setAcaoPorFoto] = useState<Readonly<Record<string, AcaoNaFoto>>>({});
  const [recortando, setRecortando] = useState<FotoDoSignatarioData | null>(null);
  const [salvandoRecorte, setSalvandoRecorte] = useState(false);

  const recarregar = useCallback(async (): Promise<FotosDoSignatarioData> => {
    const lidas = await listarFotosDoSignatario(client, caseId);
    setDados(lidas);
    onMudou?.();
    return lidas;
  }, [client, caseId, onMudou]);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const lidas = await listarFotosDoSignatario(client, caseId);
        if (!cancelado) setDados(lidas);
      } catch {
        if (!cancelado) setErro(t.erroCarregar);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [client, caseId]);

  const marcarAcao = useCallback((fotoId: string, acao: AcaoNaFoto | null) => {
    setAcaoPorFoto((atuais) => {
      const { [fotoId]: anterior, ...resto } = atuais;
      void anterior;
      return acao === null ? resto : { ...resto, [fotoId]: acao };
    });
  }, []);

  const anexar = (grupo: GrupoDaFoto, arquivo: File): void => {
    if (enviandoNoGrupo !== null) return;
    void (async () => {
      setEnviandoNoGrupo(grupo);
      setErro(null);
      try {
        await anexarFotoDoSignatario(client, caseId, grupo, arquivo);
        await recarregar();
      } catch (err) {
        setErro(mensagemDoErro(err));
      } finally {
        setEnviandoNoGrupo(null);
      }
    })();
  };

  const executarNaFoto = (
    foto: FotoDoSignatarioData,
    acao: AcaoNaFoto,
    chamada: () => Promise<unknown>,
  ): void => {
    void (async () => {
      marcarAcao(foto.id, acao);
      setErro(null);
      try {
        await chamada();
        await recarregar();
      } catch (err) {
        setErro(mensagemDoErro(err));
      } finally {
        marcarAcao(foto.id, null);
      }
    })();
  };

  const aplicarRecorte = (caixa: CaixaDoRecorte | null, rotacao: RotacaoDaFoto): void => {
    const alvo = recortando;
    if (alvo === null) return;
    void (async () => {
      setSalvandoRecorte(true);
      setErro(null);
      try {
        await recortarFotoDoSignatario(client, caseId, alvo.id, {
          bounding_box: caixa,
          rotacao_deg: rotacao,
        });
        await recarregar();
        setRecortando(null);
      } catch (err) {
        setErro(mensagemDoErro(err));
      } finally {
        setSalvandoRecorte(false);
      }
    })();
  };

  const emVoo = enviandoNoGrupo !== null || Object.keys(acaoPorFoto).length > 0;

  return (
    <Blueprint
      as="section"
      className="space-y-sc-4 p-sc-6"
      data-testid="fotos-do-signatario"
      aria-labelledby="fotos-do-signatario-titulo"
    >
      <div>
        <h3 id="fotos-do-signatario-titulo" className="text-body-lg text-fg">
          {t.titulo}
        </h3>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.intro}</p>
      </div>

      {erro !== null ? (
        <p role="alert" className="text-fg-danger text-body-sm" data-testid="fotos-erro">
          {erro}
        </p>
      ) : null}

      {dados !== null
        ? dados.grupos.map((grupo) => (
            <GrupoDeFotos
              key={grupo.grupo}
              caseId={caseId}
              grupo={grupo}
              enviando={enviandoNoGrupo === grupo.grupo}
              bloqueado={concluido || emVoo}
              acaoPorFoto={acaoPorFoto}
              onAnexar={(arquivo) => anexar(grupo.grupo, arquivo)}
              onEscolher={(foto) =>
                executarNaFoto(foto, 'escolhendo', () =>
                  elegerFotoDoSignatario(client, caseId, foto.id),
                )
              }
              onRecortar={setRecortando}
              onRemover={(foto) =>
                executarNaFoto(foto, 'removendo', () =>
                  removerFotoDoSignatario(client, caseId, foto.id),
                )
              }
            />
          ))
        : null}

      {recortando !== null ? (
        <div className="border-t border-border pt-sc-4">
          <RecorteDaFoto
            caseId={caseId}
            foto={recortando}
            salvando={salvandoRecorte}
            onAplicar={aplicarRecorte}
            onCancelar={() => setRecortando(null)}
          />
        </div>
      ) : null}

      <p className="max-w-prose text-caption text-fg-dim" data-testid="fotos-sigilo">
        {t.sigilo}
      </p>
    </Blueprint>
  );
}
